// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import { IButton, mergeStyles, Stack } from '@fluentui/react';
import { _isInCall, _isInLobbyOrConnecting } from '@internal/calling-component-bindings';
import { _ComplianceBanner, _ComplianceBannerProps, _DrawerMenu, _DrawerMenuItemProps, _useContainerHeight, _useContainerWidth, ActiveErrorMessage, ErrorBar, ErrorBarProps, useTheme } from '@internal/react-components';
/* @conditional-compile-remove(gallery-layouts) */
import { VideoGalleryLayout } from '@internal/react-components';
import React, { useMemo, useRef, useState } from 'react';
import { useEffect } from 'react';
import { useCallback } from 'react';
/* @conditional-compile-remove(one-to-n-calling) @conditional-compile-remove(PSTN-calls) */ /* @conditional-compile-remove(close-captions) */
import { AvatarPersonaDataCallback } from '../../common/AvatarPersona';
/* @conditional-compile-remove(close-captions) */
import { CaptionsBanner } from '../../common/CaptionsBanner';
import { containerDivStyles } from '../../common/ContainerRectProps';
import { compositeMinWidthRem } from '../../common/styles/Composite.styles';
import { useAdapter } from '../adapter/CallAdapterProvider';
import { CallControls, CallControlsProps } from '../components/CallControls';
import { CommonCallControlBar } from '../../common/ControlBar/CommonCallControlBar';
import { callArrangementContainerStyles, notificationsContainerStyles, containerStyleDesktop, containerStyleMobile, mediaGalleryContainerStyles, galleryParentContainerStyles, bannerNotificationStyles, CONTROL_BAR_Z_INDEX, DRAWER_Z_INDEX } from '../styles/CallPage.styles';
import { MutedNotification, MutedNotificationProps } from './MutedNotification';
import { CallAdapter } from '../adapter';
import { useSelector } from '../hooks/useSelector';
import { callStatusSelector } from '../selectors/callStatusSelector';
import { CallControlOptions } from '../types/CallControlOptions';
import { PreparedMoreDrawer } from '../../common/Drawer/PreparedMoreDrawer';
/* @conditional-compile-remove(close-captions) */
import { getCallStatus, getIsTeamsCall, getCaptionsStatus } from '../selectors/baseSelectors';
import { drawerContainerStyles } from '../styles/CallComposite.styles';
import { SidePane } from './SidePane/SidePane';
import { usePeoplePane } from './SidePane/usePeoplePane';
/* @conditional-compile-remove(video-background-effects) */
import { useVideoEffectsPane, VIDEO_EFFECTS_SIDE_PANE_ID, VIDEO_EFFECTS_SIDE_PANE_WIDTH_REM } from './SidePane/useVideoEffectsPane';
import { isDisabled } from '../utils';
import { SidePaneRenderer, useIsSidePaneOpen } from './SidePane/SidePaneProvider';
/* @conditional-compile-remove(video-background-effects) */
import { useIsParticularSidePaneOpen } from './SidePane/SidePaneProvider';
import { ModalLocalAndRemotePIP } from '../../common/ModalLocalAndRemotePIP';
import { getPipStyles } from '../../common/styles/ModalLocalAndRemotePIP.styles';
import { useMinMaxDragPosition } from '../../common/utils';
import { MobileChatSidePaneTabHeaderProps } from '../../common/TabHeader';
import { CommonCallControlOptions } from '../../common/types/CommonCallControlOptions';
/* @conditional-compile-remove(video-background-effects) */
import { localVideoSelector } from '../../CallComposite/selectors/localVideoStreamSelector';
/* @conditional-compile-remove(capabilities) */
import { CapabilitiesChangedNotificationBar, CapabilitiesChangeNotificationBarProps } from './CapabilitiesChangedNotificationBar';
import { useLocale } from '../../localization';

/**
 * @private
 */
export interface CallArrangementProps {
  id?: string;
  complianceBannerProps: _ComplianceBannerProps;
  errorBarProps: ErrorBarProps | false;
  mutedNotificationProps?: MutedNotificationProps;
  callControlProps: CallControlsProps;
  onRenderGalleryContent: () => JSX.Element;
  dataUiId: string;
  mobileView: boolean;
  modalLayerHostId: string;
  /* @conditional-compile-remove(one-to-n-calling) @conditional-compile-remove(PSTN-calls) */ /* @conditional-compile-remove(close-captions) */
  onFetchAvatarPersonaData?: AvatarPersonaDataCallback;
  updateSidePaneRenderer: (renderer: SidePaneRenderer | undefined) => void;
  mobileChatTabHeader?: MobileChatSidePaneTabHeaderProps;
  latestErrors: ActiveErrorMessage[];
  onDismissError: (error: ActiveErrorMessage) => void;
  /* @conditional-compile-remove(gallery-layouts) */
  onUserSetOverflowGalleryPositionChange?: (position: 'Responsive' | 'horizontalTop') => void;
  /* @conditional-compile-remove(gallery-layouts) */
  onUserSetGalleryLayoutChange?: (layout: VideoGalleryLayout) => void;
  /* @conditional-compile-remove(gallery-layouts) */
  userSetGalleryLayout?: VideoGalleryLayout;
  /* @conditional-compile-remove(capabilities) */
  capabilitiesChangedNotificationBarProps?: CapabilitiesChangeNotificationBarProps;
  onCloseChatPane?: () => void;
}

/**
 * @private
 */
export const CallArrangement = (props: CallArrangementProps): JSX.Element => {
  const containerClassName = useMemo(() => {
    return props.mobileView ? containerStyleMobile : containerStyleDesktop;
  }, [props.mobileView]);
  const theme = useTheme();
  const callGalleryStyles = useMemo(() => galleryParentContainerStyles(theme.palette.neutralLighterAlt), [theme.palette.neutralLighterAlt]);
  const peopleButtonRef = useRef<IButton>(null);
  const cameraButtonRef = useRef<IButton>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const containerWidth = _useContainerWidth(containerRef);
  const containerHeight = _useContainerHeight(containerRef);
  const isInLobby = _isInLobbyOrConnecting(useSelector(callStatusSelector).callStatus);
  const {
    updateSidePaneRenderer
  } = props;
  const adapter = useAdapter();
  const [drawerMenuItems, setDrawerMenuItems] = useState<_DrawerMenuItemProps[]>([]);
  const peoplePaneProps = useMemo(() => ({
    updateSidePaneRenderer,
    setDrawerMenuItems,
    inviteLink: props.callControlProps.callInvitationURL,
    onFetchParticipantMenuItems: props.callControlProps?.onFetchParticipantMenuItems,
    mobileView: props.mobileView,
    peopleButtonRef
  }), [updateSidePaneRenderer, props.callControlProps.callInvitationURL, props.callControlProps?.onFetchParticipantMenuItems, props.mobileView, peopleButtonRef]);
  const {
    isPeoplePaneOpen,
    openPeoplePane,
    closePeoplePane
  } = usePeoplePane(peoplePaneProps);
  const togglePeoplePane = useCallback(() => {
    if (isPeoplePaneOpen) {
      closePeoplePane();
    } else {
      openPeoplePane();
    }
  }, [closePeoplePane, isPeoplePaneOpen, openPeoplePane]);
  const isSidePaneOpen = useIsSidePaneOpen();
  const [renderGallery, setRenderGallery] = useState<boolean>(!isSidePaneOpen && props.mobileView);
  useEffect(() => {
    if (isSidePaneOpen && props.mobileView) {
      setRenderGallery(false);
    } else {
      setRenderGallery(true);
    }
  }, [props.mobileView, isSidePaneOpen]);
  const locale = useLocale();
  const modalStrings = {
    dismissModalAriaLabel: locale.strings.call.dismissModalAriaLabel
  };
  const isMobileWithActivePane = props.mobileView && isSidePaneOpen;
  const callCompositeContainerCSS = useMemo((): React.CSSProperties => {
    return {
      display: isMobileWithActivePane ? 'none' : 'flex',
      minWidth: props.mobileView ? 'unset' : `${compositeMinWidthRem}rem`,
      width: '100%',
      height: '100%',
      position: 'relative'
    };
  }, [isMobileWithActivePane, props.mobileView]);
  /* @conditional-compile-remove(video-background-effects) */
  const onResolveVideoEffectDependency = adapter.getState().onResolveVideoEffectDependency;

  /* @conditional-compile-remove(video-background-effects) */
  const {
    openVideoEffectsPane
  } = useVideoEffectsPane(props.updateSidePaneRenderer, props.mobileView, props.latestErrors, props.onDismissError, cameraButtonRef);
  const [showDrawer, setShowDrawer] = useState(false);
  const onMoreButtonClicked = useCallback(() => {
    setShowDrawer(true);
  }, []);
  const closeDrawer = useCallback(() => {
    setShowDrawer(false);
  }, []);
  const onMoreDrawerPeopleClicked = useCallback(() => {
    setShowDrawer(false);
    togglePeoplePane();
  }, [togglePeoplePane]);
  const drawerContainerStylesValue = useMemo(() => drawerContainerStyles(DRAWER_Z_INDEX), []);
  let canUnmute = true;
  let filteredLatestErrors: ActiveErrorMessage[] = props.errorBarProps !== false ? props.latestErrors : [];

  /* @conditional-compile-remove(video-background-effects) */
  const isCameraOn = useSelector(localVideoSelector).isAvailable;
  /* @conditional-compile-remove(video-background-effects) */
  const isVideoPaneOpen = useIsParticularSidePaneOpen(VIDEO_EFFECTS_SIDE_PANE_ID);
  /* @conditional-compile-remove(video-background-effects) */
  if ((isVideoPaneOpen || !isCameraOn) && props.errorBarProps) {
    filteredLatestErrors = filteredLatestErrors.filter(e => e.type !== 'unableToStartVideoEffect');
  }

  /* @conditional-compile-remove(close-captions) */
  const isTeamsCall = useSelector(getIsTeamsCall);
  /* @conditional-compile-remove(close-captions) */
  const hasJoinedCall = useSelector(getCallStatus) === 'Connected';
  /* @conditional-compile-remove(close-captions) */
  const isCaptionsOn = useSelector(getCaptionsStatus);
  const minMaxDragPosition = useMinMaxDragPosition(props.modalLayerHostId);
  const pipStyles = useMemo(() => getPipStyles(theme), [theme]);
  const verticalControlBar = props.mobileView && containerWidth && containerHeight && containerWidth / containerHeight > 1 ? true : false;

  /* @conditional-compile-remove(capabilities) */
  // Filter out shareScreen capability notifications if on mobile
  const filteredCapabilitesChangedNotifications = props.mobileView ? props.capabilitiesChangedNotificationBarProps?.capabilitiesChangedNotifications.filter(notification => notification.capabilityName !== 'shareScreen') : props.capabilitiesChangedNotificationBarProps?.capabilitiesChangedNotifications;
  return <div ref={containerRef} className={mergeStyles(containerDivStyles)} id={props.id}>
      <Stack verticalFill horizontalAlign="stretch" className={containerClassName} data-ui-id={props.dataUiId}>
        <Stack reversed horizontal={verticalControlBar} grow styles={callArrangementContainerStyles(verticalControlBar)}>
          {props.callControlProps?.options !== false && !isMobileWithActivePane && <Stack verticalAlign={'center'} className={mergeStyles({
          zIndex: CONTROL_BAR_Z_INDEX,
          padding: verticalControlBar ? '0.25rem' : 'unset'
        })}>
              {isLegacyCallControlEnabled(props.callControlProps?.options) ? <CallControls {...props.callControlProps} containerWidth={containerWidth} containerHeight={containerHeight} isMobile={props.mobileView} displayVertical={verticalControlBar} /> : <CommonCallControlBar {...props.callControlProps} callControls={props.callControlProps.options} callAdapter={(adapter as CallAdapter)} mobileView={props.mobileView} disableButtonsForLobbyPage={isInLobby} peopleButtonChecked={isPeoplePaneOpen} onPeopleButtonClicked={togglePeoplePane} onMoreButtonClicked={onMoreButtonClicked}
          /* @conditional-compile-remove(close-captions) */ isCaptionsSupported={isTeamsCall && hasJoinedCall}
          /* @conditional-compile-remove(close-captions) */ isCaptionsOn={isCaptionsOn}
          /* @conditional-compile-remove(video-background-effects) */ onClickVideoEffects={onResolveVideoEffectDependency ? openVideoEffectsPane : undefined} displayVertical={verticalControlBar}
          /* @conditional-compile-remove(gallery-layouts) */ onUserSetOverflowGalleryPositionChange={props.onUserSetOverflowGalleryPositionChange}
          /* @conditional-compile-remove(gallery-layouts) */ onUserSetGalleryLayout={props.onUserSetGalleryLayoutChange}
          /* @conditional-compile-remove(gallery-layouts) */ userSetGalleryLayout={props.userSetGalleryLayout} peopleButtonRef={peopleButtonRef} cameraButtonRef={cameraButtonRef} />}
            </Stack>}
          {props.callControlProps?.options !== false && showDrawer && <Stack styles={drawerContainerStylesValue}>
              <PreparedMoreDrawer callControls={props.callControlProps.options} onLightDismiss={closeDrawer} onPeopleButtonClicked={onMoreDrawerPeopleClicked} /* @conditional-compile-remove(close-captions) */
          isCaptionsSupported={isTeamsCall && hasJoinedCall}
          /* @conditional-compile-remove(gallery-layouts) */ onUserSetGalleryLayout={props.onUserSetGalleryLayoutChange}
          /* @conditional-compile-remove(gallery-layouts) */ userSetGalleryLayout={props.userSetGalleryLayout} />
            </Stack>}

          {}
          <Stack horizontal grow>
            <Stack.Item style={callCompositeContainerCSS}>
              <Stack.Item styles={callGalleryStyles} grow>
                <Stack verticalFill styles={mediaGalleryContainerStyles}>
                  <Stack.Item styles={notificationsContainerStyles}>
                    <Stack styles={bannerNotificationStyles}>
                      <_ComplianceBanner {...props.complianceBannerProps} />
                    </Stack>
                    {props.errorBarProps !== false && <Stack styles={bannerNotificationStyles}>
                        <ErrorBar {...props.errorBarProps} onDismissError={props.onDismissError} activeErrorMessages={filteredLatestErrors} />
                      </Stack>}
                    {/* @conditional-compile-remove(capabilities) */
                  props.capabilitiesChangedNotificationBarProps && props.capabilitiesChangedNotificationBarProps.capabilitiesChangedNotifications.length > 0 && <Stack styles={bannerNotificationStyles}>
                            <CapabilitiesChangedNotificationBar {...props.capabilitiesChangedNotificationBarProps} capabilitiesChangedNotifications={filteredCapabilitesChangedNotifications ?? []} />
                          </Stack>}
                    {canUnmute && !!props.mutedNotificationProps && <MutedNotification {...props.mutedNotificationProps} />}
                  </Stack.Item>
                  {renderGallery && props.onRenderGalleryContent && props.onRenderGalleryContent()}
                  {/* @conditional-compile-remove(close-captions) */
                true && <CaptionsBanner isMobile={props.mobileView} onFetchAvatarPersonaData={props.onFetchAvatarPersonaData} />}
                </Stack>
              </Stack.Item>
            </Stack.Item>
            <SidePane mobileView={props.mobileView}
          /* @conditional-compile-remove(video-background-effects) */ maxWidth={isVideoPaneOpen ? `${VIDEO_EFFECTS_SIDE_PANE_WIDTH_REM}rem` : undefined} updateSidePaneRenderer={props.updateSidePaneRenderer} onPeopleButtonClicked={props.mobileView && !shouldShowPeopleTabHeaderButton(props.callControlProps.options) ? undefined : togglePeoplePane} disablePeopleButton={typeof props.callControlProps.options !== 'boolean' && isDisabled(props.callControlProps.options?.participantsButton)} onChatButtonClicked={props.mobileChatTabHeader?.onClick} disableChatButton={props.mobileChatTabHeader?.disabled} />
            {props.mobileView && <ModalLocalAndRemotePIP modalLayerHostId={props.modalLayerHostId} hidden={!isSidePaneOpen} styles={pipStyles} strings={modalStrings} minDragPosition={minMaxDragPosition.minDragPosition} maxDragPosition={minMaxDragPosition.maxDragPosition} onDismissSidePane={() => {
            closePeoplePane();
            if (props.onCloseChatPane) {
              props.onCloseChatPane();
            }
          }} />}
            {drawerMenuItems.length > 0 && <Stack styles={drawerContainerStyles()}>
                <_DrawerMenu onLightDismiss={() => setDrawerMenuItems([])} items={drawerMenuItems} />
              </Stack>}
          </Stack>
        </Stack>
      </Stack>
    </div>;
};
const isLegacyCallControlEnabled = (options?: boolean | CallControlOptions): boolean => {
  return !!options && options !== true && options?.legacyControlBarExperience === true;
};
const shouldShowPeopleTabHeaderButton = (callControls?: boolean | CommonCallControlOptions): boolean => {
  if (callControls === undefined || callControls === true) {
    return true;
  }
  if (callControls === false) {
    return false;
  }
  return callControls.participantsButton !== false && callControls.peopleButton !== false;
};"../../../../../calling-component-bindings/src""../../../../../react-components/src"